import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DollarSign, ShoppingBag, TrendingUp, Ticket, CreditCard, FileDown, Landmark, Receipt } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadFile } from "@/lib/download";
import Pager from "@/components/Pager";
import BrokerIncomePanel from "@/pages/admin/BrokerIncomePanel";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminFinance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [custPage, setCustPage] = useState(1);
  const [orderPage, setOrderPage] = useState(1);
  const [provider, setProvider] = useState("all");
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  useEffect(() => {
    api.get("/admin/finance/overview").then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load finance"))
      .finally(() => setLoading(false));
  }, []);

  const cards = [
    { k: "Combined revenue", v: money(data?.combined_revenue), icon: DollarSign, hint: "Products + broker income" },
    { k: "Product revenue", v: money(data?.total_revenue), icon: ShoppingBag },
    { k: "Broker income", v: money(data?.broker_income), icon: Landmark },
    { k: "Paid orders", v: data?.paid_orders ?? 0, icon: Receipt },
    { k: "Avg order value", v: money(data?.avg_order_value), icon: TrendingUp },
    { k: "Discounts given", v: money(data?.total_discount), icon: Ticket },
  ];

  const maxMonth = Math.max(1, ...(data?.monthly || []).map((m) => m.revenue));

  const filteredOrders = (data?.recent_orders || []).filter((o) => {
    if (provider !== "all" && o.provider !== provider) return false;
    if (search && !(`${o.email} ${o.items.join(" ")}`.toLowerCase().includes(search.toLowerCase()))) return false;
    const d = (o.paid_at || "").slice(0, 10);
    if (start && d && d < start) return false;
    if (end && d && d > end) return false;
    return true;
  });

  const exportCsv = () => {
    const q = new URLSearchParams();
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    if (provider !== "all") q.set("provider", provider);
    q.set("status", "paid");
    downloadFile(`/admin/finance/export.csv?${q.toString()}`, "financials.csv");
  };

  return (
    <div data-testid="admin-finance">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Finance</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Money dashboard</h1>
          <p className="mt-2 max-w-xl text-sm text-emerald-100/60">Revenue, payment sources and your top customers at a glance.</p>
        </div>
        <button onClick={exportCsv} data-testid="finance-export-csv" className="btn-edge text-sm"><FileDown size={14} /> Export CSV</button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, i) => (
          <motion.div key={c.k} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="card-edge p-5" data-testid={`finance-card-${i}`}>
            <div className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-300">
              <c.icon size={16} />
            </div>
            <p className="overline">{c.k}</p>
            <p className="mt-1 font-editorial text-3xl text-white">{loading ? "…" : c.v}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* Monthly revenue */}
        <div className="card-edge p-6">
          <p className="overline mb-5">Revenue — last months</p>
          {(!data?.monthly || data.monthly.length === 0) ? (
            <p className="text-sm text-emerald-100/50">No paid orders yet.</p>
          ) : (
            <div className="grid gap-3">
              {data.monthly.map((m) => (
                <div key={m.month} className="flex items-center gap-3" data-testid={`finance-month-${m.month}`}>
                  <span className="w-16 font-mono-edge text-[11px] text-emerald-100/50">{m.month}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-emerald-500/10">
                    <div className="h-full rounded-full bg-lime-400/70" style={{ width: `${(m.revenue / maxMonth) * 100}%` }} />
                  </div>
                  <span className="w-24 text-right text-sm text-white">{money(m.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* By provider */}
        <div className="card-edge p-6">
          <p className="overline mb-5">By payment source</p>
          {(!data?.by_provider || data.by_provider.length === 0) ? (
            <p className="text-sm text-emerald-100/50">No revenue yet.</p>
          ) : (
            <div className="grid gap-3">
              {data.by_provider.map((p) => (
                <div key={p.provider} className="flex items-center justify-between rounded-lg border border-emerald-500/12 bg-[#0d2416]/60 p-3" data-testid={`finance-provider-${p.provider}`}>
                  <div className="flex items-center gap-2">
                    <CreditCard size={14} className="text-emerald-300" />
                    <span className="text-sm capitalize text-white">{p.provider}</span>
                    <span className="text-[11px] text-emerald-100/50">· {p.orders} order{p.orders === 1 ? "" : "s"}</span>
                  </div>
                  <span className="font-editorial text-lime-300">{money(p.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Top customers */}
        <div className="card-edge overflow-hidden">
          <div className="border-b border-emerald-500/10 px-6 py-4"><p className="overline">Top customers by spend</p></div>
          {(!data?.top_customers || data.top_customers.length === 0) ? (
            <p className="p-6 text-sm text-emerald-100/50">No customers yet.</p>
          ) : (
            <>
            <table className="min-w-full text-left text-sm">
              <tbody>
                {data.top_customers.slice((custPage - 1) * 20, custPage * 20).map((c) => (
                  <tr key={c.user_id} className="border-b border-emerald-500/8 last:border-0" data-testid={`finance-customer-${c.email}`}>
                    <td className="px-6 py-3">
                      <p className="text-white">{c.name || "—"}</p>
                      <p className="text-[11px] text-emerald-100/50">{c.email}</p>
                    </td>
                    <td className="px-6 py-3 text-right text-emerald-100/60">{c.orders} order{c.orders === 1 ? "" : "s"}</td>
                    <td className="px-6 py-3 text-right font-editorial text-lime-300">{money(c.spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager page={custPage} pages={Math.max(1, Math.ceil(data.top_customers.length / 20))} total={data.top_customers.length} onPage={setCustPage} testId="finance-customers-pager" />
            </>
          )}
        </div>

        {/* Recent orders */}
        <div className="card-edge overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-500/10 px-6 py-4">
            <p className="overline">Recent paid orders</p>
            <div className="flex flex-wrap items-center gap-2">
              <input value={search} onChange={(e) => { setSearch(e.target.value); setOrderPage(1); }} placeholder="Search…" data-testid="finance-search"
                className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2.5 py-1.5 text-xs text-white focus:border-emerald-400/60 focus:outline-none" />
              <select value={provider} onChange={(e) => { setProvider(e.target.value); setOrderPage(1); }} data-testid="finance-provider-filter"
                className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2 py-1.5 text-xs text-white">
                <option value="all">All sources</option>
                {(data?.by_provider || []).map((p) => <option key={p.provider} value={p.provider}>{p.provider}</option>)}
              </select>
              <input type="date" value={start} onChange={(e) => { setStart(e.target.value); setOrderPage(1); }} data-testid="finance-start"
                className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2 py-1.5 text-xs text-white" />
              <input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setOrderPage(1); }} data-testid="finance-end"
                className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2 py-1.5 text-xs text-white" />
            </div>
          </div>
          {filteredOrders.length === 0 ? (
            <p className="p-6 text-sm text-emerald-100/50">No orders match.</p>
          ) : (
            <>
            <div className="divide-y divide-emerald-500/8">
              {filteredOrders.slice((orderPage - 1) * 20, orderPage * 20).map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-3 px-6 py-3" data-testid={`finance-order-${o.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm text-white">{o.items.join(", ")}</p>
                    <p className="text-[11px] text-emerald-100/50">
                      {o.email} · <span className="capitalize">{o.provider}</span>{o.coupon_code ? ` · ${o.coupon_code}` : ""} · {o.paid_at ? new Date(o.paid_at).toLocaleDateString() : ""}
                    </p>
                  </div>
                  <span className="font-editorial text-lime-300">{money(o.total)}</span>
                </div>
              ))}
            </div>
            <Pager page={orderPage} pages={Math.max(1, Math.ceil(filteredOrders.length / 20))} total={filteredOrders.length} onPage={setOrderPage} testId="finance-orders-pager" />
            </>
          )}
        </div>
      </div>

      <BrokerIncomePanel />
    </div>
  );
}
